import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Facebook, Linkedin, Share2, X, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { useNewsletterSubscription } from "@/hooks/useNewsletterSubscription";
import AnySkillLogo from "./AnySkillLogo";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const { subscribe, isLoading } = useNewsletterSubscription();
  
  // Pre-written content for social media posts
  const shareContent = {
    title: "AnySkill - Talent Marketplace",
    text: "Discover AnySkill - the talent marketplace where anyone can find affordable help on projects and everyone can monetize their talents! 💼✨ It empowers all to be entrepreneurs and teaches students how to create value doing something they're good at. #TalentMarketplace #Entrepreneurship #StudentSuccess",
    hashtags: "#TalentMarketplace #Entrepreneurship #StudentSuccess #Freelance #Skills"
  };

  const shareOnLinkedIn = () => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(shareContent.text);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}&text=${text}`, '_blank');
  };

  const shareOnX = () => {
    const url = window.location.href;
    const text = `${shareContent.text}\n\n${shareContent.hashtags}\n\n${url}`;
    const encodedText = encodeURIComponent(text);
    window.open(`https://twitter.com/intent/tweet?text=${encodedText}`, '_blank');
  };

  const shareOnFacebook = () => {
    const facebookPost = `${shareContent.text}\n\nVisit: ${window.location.href}\n\n${shareContent.hashtags}`;
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(facebookPost)
        .then(() => {
          alert('Facebook post content copied to clipboard! Paste it when creating your Facebook post.');
          window.open('https://www.facebook.com/', '_blank');
        })
        .catch((err) => {
          console.error('Could not copy text: ', err);
          alert('Please copy this text for your Facebook post:\n\n' + facebookPost);
          window.open('https://www.facebook.com/', '_blank');
        });
    } else {
      alert('Please copy this text for your Facebook post:\n\n' + facebookPost);
      window.open('https://www.facebook.com/', '_blank');
    }
  };

  const shareOnInstagram = () => {
    const instagramPost = `${shareContent.text}\n\nVisit: ${window.location.href}\n\n${shareContent.hashtags}`;
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(instagramPost)
        .then(() => {
          alert('Instagram post content copied to clipboard! Paste it when creating your Instagram post.');
          window.open('https://www.instagram.com/', '_blank');
        })
        .catch((err) => {
          console.error('Could not copy text: ', err);
          alert('Please copy this text for your Instagram post:\n\n' + instagramPost);
          window.open('https://www.instagram.com/', '_blank');
        });
    } else {
      alert('Please copy this text for your Instagram post:\n\n' + instagramPost);
      window.open('https://www.instagram.com/', '_blank');
    }
  };

  const shareGeneric = () => {
    if (navigator.share) {
      navigator.share({
        title: shareContent.title,
        text: shareContent.text,
        url: window.location.href,
      })
      .catch((error) => console.log('Error sharing', error));
    } else {
      const shareText = `${shareContent.text}\n\nVisit: ${window.location.href}`;
      navigator.clipboard.writeText(shareText)
        .then(() => alert('Link and message copied to clipboard!'))
        .catch((err) => console.error('Could not copy text: ', err));
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await subscribe(email);
    if (success) {
      setEmail("");
    }
  };

  return (
    <footer className="bg-gray-900 text-white pt-10 pb-8">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between gap-8 mb-8">
          <div>
            <h3 className="font-poppins font-semibold mb-3">Share</h3>
            <div className="flex items-center space-x-4">
              <button 
                onClick={shareOnFacebook} 
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Share on Facebook"
                title="Copy Facebook post content and open Facebook"
              >
                <Facebook className="w-5 h-5" />
              </button>
              <button 
                onClick={shareOnLinkedIn} 
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Share on LinkedIn"
                title="Share on LinkedIn with professional message"
              >
                <Linkedin className="w-5 h-5" />
              </button>
              <button 
                onClick={shareOnX} 
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Share on X"
                title="Share on X (Twitter) with engaging tweet"
              >
                <X className="w-5 h-5" />
              </button>
              <button 
                onClick={shareOnInstagram} 
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Share on Instagram"
                title="Copy Instagram post content and open Instagram"
              >
                <Instagram className="w-5 h-5" />
              </button>
              <button
                onClick={shareGeneric}
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Share"
                title="Share with native sharing or copy to clipboard"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div>
              <h3 className="font-poppins font-semibold mb-3">For Providers</h3>
              <ul className="font-inter space-y-2">
                <li><Link to="/how-to-start" className="text-gray-400 hover:text-white transition-colors">How to Start</Link></li>
                <li><Link to="/safety-guidelines" className="text-gray-400 hover:text-white transition-colors">Safety Guidelines</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-poppins font-semibold mb-3">For Clients</h3>
              <ul className="font-inter space-y-2">
                <li><Link to="/how-to-hire" className="text-gray-400 hover:text-white transition-colors">How to Hire</Link></li>
                <li><Link to="/success-stories" className="text-gray-400 hover:text-white transition-colors">Success Stories</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-poppins font-semibold mb-3">Subscribe</h3>
              <form onSubmit={handleNewsletterSubmit} className="flex">
                <input 
                  type="email" 
                  placeholder="Your email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-gray-800 text-white px-4 py-2 rounded-l-md w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                  disabled={isLoading}
                />
                <Button 
                  type="submit"
                  disabled={isLoading}
                  className="bg-blue-600 hover:bg-blue-700 rounded-l-none"
                >
                  {isLoading ? "..." : "Send"}
                </Button>
              </form>
            </div>
          </div>
        </div>
        
        <div className="border-t border-gray-800 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-4 md:mb-0">
              <AnySkillLogo size={24} />
              <p className="font-inter text-gray-400">
                &copy; {currentYear} AnySkill. All rights reserved.
              </p>
            </div>
            <div className="flex space-x-6">
              <Link to="/terms-of-service" className="font-inter text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/privacy-policy" className="font-inter text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
